/**
 * Normalizes CMS HTML from `/api/about_content`: layout (left align), and drops
 * noisy CMS-only rows (Version build line, Invite Tutorial link) — other copy unchanged.
 */
function normalizeText(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

/** Matches e.g. "Version: 001.009 - February 10, 2023" from CMS `<p><strong>Version:</strong> ...` */
function isVersionOnlyParagraph(text: string): boolean {
  const t = normalizeText(text);
  if (!t) return false;
  if (!/version\s*:/i.test(t)) return false;
  if (t.length > 220) return false;
  return /\d+\.\d+(?:\.\d+)?/.test(t) || /\b20\d{2}\b/.test(t);
}

/** YouTube / CMS "Liberty Rent Invite Tutorial (2 mins)" row */
function isInviteTutorialParagraph(text: string): boolean {
  const t = normalizeText(text);
  return /liberty\s+rent\s+invite\s+tutorial/i.test(t);
}

export function normalizeHelpAboutHtmlLayout(html: string): string {
  if (!html || !html.trim()) return html;

  const doc = new DOMParser().parseFromString(
    `<div data-help-about-root>${html}</div>`,
    "text/html",
  );
  const root = doc.querySelector("[data-help-about-root]");
  if (!root) return html;

  root.querySelectorAll("p").forEach((p) => {
    const text = p.textContent || "";
    if (isVersionOnlyParagraph(text) || isInviteTutorialParagraph(text)) p.remove();
  });

  root.querySelectorAll("div").forEach((div) => {
    const text = (div.textContent || "").trim();
    if (text.length >= 220) return;
    if (isVersionOnlyParagraph(text) || isInviteTutorialParagraph(text)) div.remove();
  });

  root.querySelectorAll("[style]").forEach((el) => {
    let s = el.getAttribute("style") || "";
    if (/text-align\s*:\s*center/i.test(s)) {
      s = s.replace(/text-align\s*:\s*center[^;]*/gi, "text-align:left");
    }
    s = s.trim().replace(/^;+|;+$/g, "");
    if (s) el.setAttribute("style", s);
    else el.removeAttribute("style");
  });

  root.querySelectorAll("[align]").forEach((el) => {
    const a = (el.getAttribute("align") || "").toLowerCase();
    if (a === "center" || a === "middle") {
      el.setAttribute("align", "left");
    }
  });

  root.querySelectorAll("center").forEach((center) => {
    const div = doc.createElement("div");
    div.setAttribute("style", "text-align:left");
    div.innerHTML = center.innerHTML;
    center.parentNode?.replaceChild(div, center);
  });

  return root.innerHTML;
}

/** @deprecated Use normalizeHelpAboutHtmlLayout — kept for any stray imports */
export const transformHelpAboutHtml = normalizeHelpAboutHtmlLayout;
